import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Home, Award, Lock } from "lucide-react";
import { LogoMark, LogoLockup } from "@/components/Logo";

import BookmarksDrawer from "@/components/BookmarksDrawer";
import SearchCommand from "@/components/SearchCommand";
import { useLearn } from "@/contexts/LearnContext";

export default function Layout({ children }) {
  const { user, overallProgress } = useLearn();
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const onHome = location.pathname === "/home";
  const isWelcome = location.pathname === "/";
  const pct = overallProgress();
  const courseComplete = pct >= 100;

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top progress bar */}
      {user && !isWelcome && (
        <div className="top-progress" aria-hidden>
          <div style={{ width: `${overallProgress()}%` }} />
        </div>
      )}

      {/* Header — logo on BOTH top corners */}
      {!isWelcome && (
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-6">
            {/* Left logo */}
            <Link to="/home" className="shrink-0" data-testid="header-logo-left">
              <LogoLockup size="sm" />
            </Link>

            {/* Center nav */}
            <nav className="hidden md:flex items-center gap-8 text-sm">
              {!onHome && (
                <button
                  onClick={() => navigate("/home")}
                  className="nav-link text-muted-foreground hover:text-foreground flex items-center gap-2"
                  data-testid="back-to-home-btn"
                >
                  <Home size={14} /> Back to Home
                </button>
              )}
              <button
                onClick={() => setSearchOpen(true)}
                className="nav-link text-muted-foreground hover:text-foreground flex items-center gap-2"
                data-testid="open-search-btn"
              >
                <Search size={14} /> Search
                <kbd className="hidden lg:inline ml-1 text-[10px] px-1.5 py-0.5 border border-border rounded font-mono">
                  ⌘K
                </kbd>
              </button>
              <Link
                to="/certificate"
                className={`nav-link flex items-center gap-2 ${
                  courseComplete
                    ? "text-primary font-medium"
                    : "text-muted-foreground/60 cursor-not-allowed pointer-events-none"
                }`}
                data-testid="nav-certificate"
                aria-disabled={!courseComplete}
                title={courseComplete ? "View your certificate" : "Unlocks after you complete all modules"}
              >
                {courseComplete ? <Award size={14} /> : <Lock size={14} />}
                Certificate
              </Link>
            </nav>

            {/* Right actions + logo */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchOpen(true)}
                className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-border"
                aria-label="Search"
                data-testid="mobile-search-btn"
              >
                <Search size={16} />
              </button>
              <BookmarksDrawer />

              <Link to="/home" className="ml-2 shrink-0" data-testid="header-logo-right">
                <LogoMark className="h-9 w-9" />
              </Link>
            </div>
          </div>

          {user && (
            <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-2 text-xs text-muted-foreground flex items-center justify-between">
              <span>
                Learner: <span className="text-foreground font-medium">{user.name}</span>
              </span>
              <span>
                Overall progress:{" "}
                <span className="text-primary font-semibold" data-testid="overall-progress">
                  {overallProgress()}%
                </span>
              </span>
            </div>
          )}
        </header>
      )}

      <main className="flex-1">{children}</main>

      {!isWelcome && (
        <footer className="border-t border-border mt-16 py-8 text-xs text-muted-foreground">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <LogoMark className="h-7 w-7" />
              <span>© {new Date().getFullYear()} Steadfast IT Consulting. All rights reserved.</span>
            </div>
            <div>
              Queries:{" "}
              <a
                href="mailto:operations@steadfastitconsulting.in"
                className="text-primary hover:underline"
              >
                operations@steadfastitconsulting.in
              </a>
            </div>
          </div>
        </footer>
      )}

      <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
